import { IYearMonth } from '@/coreDomainsModule/date';
import { INetworkResult } from '@/coreDomainsModule/networkResult';
import { initPagingOptions, IPagingOptions } from '@/coreDomainsModule/paging';
import { CustomerForecastRepository } from '@/coreInfrastructureModule/Repositories/CustomerForecastRepository';
import { FilterContentItem } from '@/corePagesModule/filterModal/filterModal';
import { FilterService } from '@/coreDomainsModule/services/filterService';
import { ICustomerForecastDiffLastMonthResult } from '../domains/entities/customerForecastDiffLastMonthResult';
import { DateService } from '@/coreDomainsModule/services/dateService';
import { MAX_OF_DISPLAY_FORECAST_MONTH_IN_DIFF_LAST_MONTH_PAGE } from '../constants';
import { BulkGetCustomerForecastDiffLastMonthAdapter } from '../adapter/bulkGetCustomerForecastDiffLastMonthAdapter';
import { ICustomerForecastDiffLastMonthSortInfo } from '@/corePagesModule/BaseTableContent/components/customerForecast/domains/entities/forecastDiffLastMonth';

/**
 * 前月内示差異画面の情報を取得する
 */
export class BulkGetCustomerForecastDiffLastMonthUseCase {
  private defaultPagingOptions: IPagingOptions = initPagingOptions(30);

  /**
   * APIを叩き、指定した受領月に該当する取引の前月内示差異情報を取得する
   * @param repository - リポジトリ
   * @param receivedForecastYearMonthInfo - 表示する内示の受領月
   * @param forecastTargetStartYearMonth - 内示の対象開始年月
   * @param forecastTargetMonthMaxCount - 内示の対象月数
   * @param pagingOptions - ページングオプション
   * @param sortInfo - ソート情報
   * @param isAsc - 昇順かどうか
   * @param filterItems - フィルター項目
   */
  public async Execute(
    repository: CustomerForecastRepository,
    receivedForecastYearMonthInfo: IYearMonth,
    forecastTargetStartYearMonth?: IYearMonth,
    forecastTargetMonthMaxCount?: number,
    pagingOptions?: IPagingOptions,
    sortInfo?: ICustomerForecastDiffLastMonthSortInfo,
    isAsc?: boolean,
    filterItems?: FilterContentItem[]
  ): Promise<INetworkResult<ICustomerForecastDiffLastMonthResult, Error>> {
    try {
      const usePagingOptions = pagingOptions || this.defaultPagingOptions;
      const sortType = isAsc ? 'ASC' : 'DESC';
      const useFilterItem = filterItems ? FilterService.convertCustomerForecastFilterItems(filterItems) : undefined;
      const result = await repository.getDiffLastMonth(
        sortType,
        usePagingOptions.pageSize,
        usePagingOptions.offset,
        receivedForecastYearMonthInfo.year,
        receivedForecastYearMonthInfo.month,
        useFilterItem,
        false,
        sortInfo?.sortKey,
        sortInfo?.sortTargetYearMonth?.year,
        sortInfo?.sortTargetYearMonth?.month
      );

      switch (result.status.type) {
        case 'success':
          break;
        case 'error':
          throw result.status.error;
        default:
          throw new Error('Invalid status');
      }

      const useTargetStartYearMonth =
        forecastTargetStartYearMonth ??
        DateService.getShiftedIYearMonthByMonths(receivedForecastYearMonthInfo.year, receivedForecastYearMonthInfo.month, -1);
      const useForecastTargetMonthMaxCount = forecastTargetMonthMaxCount ?? MAX_OF_DISPLAY_FORECAST_MONTH_IN_DIFF_LAST_MONTH_PAGE;

      const resultData = BulkGetCustomerForecastDiffLastMonthAdapter.adapt(
        result.status.data,
        receivedForecastYearMonthInfo,
        useTargetStartYearMonth,
        useForecastTargetMonthMaxCount
      );

      return {
        status: {
          type: 'success',
          data: resultData,
        },
      };
    } catch (error) {
      return {
        status: {
          type: 'error',
          error: Error('取得に失敗しました'),
        },
      };
    }
  }
}
